import { useMemo } from "react";
import i18next from "i18next";

import { useTranslation } from "react-i18next";
import { FONT_FAMILY } from "@/utils/font-family";
import { useGomakeTheme } from "@/hooks/use-gomake-thme";
import { useIsMobile } from "@/hooks/use-responsive";
import {
  adaptMarginLeft,
  convertWidthToVW,
  leftRightAdapter,
} from "@/utils/adapter";
import { useTheme } from "@/contexts/theme";

const useStyle = () => {
  const { t } = useTranslation();
  const dir = t("direction");
  const isMobile = useIsMobile();
  const isModern = useTheme().currentTheme === "modern";
  const { theme, secondColor, errorColor, successColor, primaryColor } =
    useGomakeTheme();
  const classes = useMemo(() => {
    return {
      mainContainer: {
        display: "flex",
        flexDirection: "column" as "column",
        paddingLeft: isModern ? 0 : isMobile ? 12 : 20,
        paddingRight: isModern ? 0 : isMobile ? 12 : 20,
        // On mobile the list scrolls inside the remaining space so pagination stays in view.
        ...(isMobile
          ? { flex: "1 1 auto", minHeight: 0, overflowX: "hidden" as const }
          : { height: "100%" }),
        overflowY: (isMobile ? "auto" : "hidden") as "auto" | "hidden",
        WebkitOverflowScrolling: "touch" as const,
        // gap: 20,
        gap: 10,
      },
      headerStyle: {
        display: "flex",
        flexDirection: (isMobile ? "column" : "row") as "column" | "row",
        flexWrap: "wrap" as "wrap",
        gap: isMobile ? 8 : 10,
        justifyContent: "space-between",
        width: "100%",
      },
      mobileTitleRowStyle: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 8,
        width: "100%",
        minHeight: 44,
        // The mobile header bar sits directly above this row.
        paddingTop: 12,
      },
      rowStyle: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 10,
        ...(isMobile
          ? { width: "100%", flexWrap: "wrap" as "wrap", minWidth: 0 }
          : {}),
      },
      filtersContainer: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 20,
        //width: "500px",
        width: "20vw",
        padding: "10px",
        flexWrap: "wrap" as "wrap",
      },
      selectedFilterContainer: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 20,
        flexWrap: "wrap" as "wrap",
      },
      statusFilterContainer: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        gap: 10,
        minWidth: "fit-content",
        width: "150px",
      },
      buttonsFilterContainer: {
        display: "flex",
        flexDirection: "column" as "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
        gap: 10,
      },
      filterLabelStyle: {
        ...FONT_FAMILY.Lexend(500, 14),
        height: 21,
      },
      textInputStyle: {
        width: "100%",
        border: "none",
      },
      searchInputStyle: {
        borderRadius: "var(--theme-radius-md, 4px)",
        width: "100%",
        borderColor: "var(--theme-input-border-color, transparent) !important",
        borderWidth: "1px",
        borderStyle: "solid !important",
        background: "var(--theme-bg-primary, #FFFFFF)",
        height: "var(--theme-input-height, 40px)",
        marginRight: "0px",
      },
      searchBtnStyle: {
        height: 40,
        backgroundColor: secondColor(500),
      },
      clearBtnStyle: {
        height: 40,
        backgroundColor: "#FFF",
        //  border: `1px solid ${secondColor(500)}`,
        color: "black",
        // width: "50%",
      },
      paginationStyle: {
        display: "flex",
        height: "50px",
        width: "100%",
        flexDirection: "row" as "row",
        justifyContent: "space-between",
      },
      warningIconStyle: {
        width: 120,
        height: 120,
        color: errorColor(300),
      },
      insideStyle: {
        width: "50%",
        height: "600px",
      },
      openBtnStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // width: "98.47px",
        height: "26px",
        padding: "4px, 12px, 4px, 12px",
        borderRadius: "4px",
        gap: "10px",
        color: successColor(300),
        //  background: successColor(300),
        ...FONT_FAMILY.Lexend(500, 14),
      },
      closeBtnStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // width: "98.47px",
        height: "fit-content",
        padding: "4px, 12px, 4px, 12px",
        borderRadius: "4px",
        gap: "10px",
        color: errorColor(300),
        //  background: errorColor(300),
        ...FONT_FAMILY.Lexend(500, 14),
      },
      createNew: {
        width: "160px",
        height: "40px",
        borderRadius: "4px",
        background: "#DCDCEC",
        color: "#101020",
        ...FONT_FAMILY.Lexend(500, 16),
        lineHeight: "20px",
        textTransform: "none" as "none",
        gap: "10px",
      },
      priceDivStyle: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 5,
      },

      /// TEST ///
      menuItemContainer: {
        display: "flex",
        flexDirection: "row" as "row",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: 8,
        padding: 15,
      },
      menuTitleStyle: {
        ...FONT_FAMILY.Lexend(500, 12),
        color: primaryColor(300),
      },
      lineStyle: {
        width: "100%",
        height: 1,
        backgroundColor: "#EEEEEE",
      },
      iconStyle: {
        position: "absolute" as "absolute",
        ...leftRightAdapter(t("direction"), 10),
      },
      cancelIconStyle: {
        width: 62,
        height: 62,
        color: "#a1a2cd",
      },
      searchInput: {
        height: 40,
        minWidth: convertWidthToVW(200),
        borderRadius: 10,
      },
      createNewDocument: {
        display: "inline-flex",
        padding: "0px 12px",
        justifyContent: "center",
        alignItems: "center",
        gap: "4px",
        borderRadius: "8px",
        border: `1px solid ${primaryColor(500)}`,
        background: "#FFF",
        boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
        textTransform: "none" as "none",
        color: primaryColor(500),
        ...FONT_FAMILY.Inter(500, 14),
        lineHeight: "18px",
        height: "40px",
        "&:hover": {
          // backgroundColor: primaryColor(100),
        },
      },
      skeltonStyle: {
        width: "180px",
        height: "100%",
        borderRadius: "8px",
      },
      inputLbl: {
        ...FONT_FAMILY.Lexend(400, 14),
        display: "flex",
        alignItems: "center",
        width: "max-content",
        ...adaptMarginLeft(dir, 8),
      },
      packageLabelStyle: {
        ...FONT_FAMILY.Lexend(550, 14),
        color: primaryColor(400),
        marginBottom: 7,
      },
      checkCellContainer: {
        overflow: "hidden",
        lineHeight: "1.4",
      },
      checkItem: {
        marginBottom: "4px",
      },
      checkItemLast: {
        marginBottom: "0",
      },
      showMoreLink: {
        cursor: "pointer",
        color: "#000",
        textDecoration: "underline",
        display: "inline-block",
        fontWeight: 500,
        fontSize: "0.9rem",
        "&:hover": { color: "#717273" },
      },
    };
  }, [i18next.language, t, theme, isMobile, isModern]);
  return {
    classes,
  };
};
export { useStyle };
